import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import type { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import {
  getVisitorConsentLinkUiState,
  resolveVisitorConsentLinkResendTarget,
  type VisitorConsentLinkRowContext
} from "./visitor-consent-link.logic";
import type { ResendVisitorConsentLinkTarget } from "../../../hooks/Remote/VisitConsent/useRemoteResendVisitorConsentLink";

type P = {
  visit: BookingScheduleInterface;
  timezone: string;
  dateFormat: string;
  linkSentManually: boolean;
  canManageConsentLink: boolean;
  isSending: boolean;
  onSendLink: (target: ResendVisitorConsentLinkTarget, rowKey: string) => void;
  visitorRegistrationId?: number;
  rowKey: string;
};

export function VisitorBookingsConsentLinkCell({
  visit,
  timezone,
  dateFormat,
  linkSentManually,
  canManageConsentLink,
  isSending,
  onSendLink,
  visitorRegistrationId,
  rowKey
}: Readonly<P>) {
  const { t } = useTranslation();

  const context: VisitorConsentLinkRowContext = {
    consentStatus: visit.consentStatus,
    linkSentManually,
    consentLinkSentAt: visit.consentLinkSentAt,
    consentCompletedAt: visit.consentCompletedAt,
    placeBookingId: visit.id,
    visitorRegistrationId
  };

  const uiState = getVisitorConsentLinkUiState(context);
  const resendTarget = resolveVisitorConsentLinkResendTarget(context);

  const handleSend = () => {
    if (!resendTarget) {
      return;
    }
    onSendLink(resendTarget, rowKey);
  };

  if (uiState.kind === "empty") {
    return null;
  }

  if (uiState.kind === "complete") {
    const completedAtLabel = formatConsentTimestamp(
      uiState.consentCompletedAt,
      timezone,
      dateFormat
    );
    return (
      <Typography variant="body2" color="text.secondary" data-testid={`${rowKey}-consent-link`}>
        {completedAtLabel
          ? t("_visitorBookings_consentLink_completedOn", { date: completedAtLabel })
          : t("_visitorBookings_consentLink_completed")}
      </Typography>
    );
  }

  if (uiState.kind === "unavailable") {
    return (
      <Typography variant="body2" color="text.secondary" data-testid={`${rowKey}-consent-link`}>
        {t("_visitorBookings_consentLink_unavailable")}
      </Typography>
    );
  }

  const buttonLabel =
    uiState.kind === "sent"
      ? t("_visitorBookings_consentLink_resend")
      : t("_visitorBookings_consentLink_send");

  const sentAtLabel =
    uiState.kind === "sent"
      ? formatConsentTimestamp(uiState.consentLinkSentAt, timezone, dateFormat)
      : null;
  const hideConsentLinkAction = visit.checkedIn === true;

  if (!canManageConsentLink) {
    return (
      <Typography variant="body2" color="text.secondary" data-testid={`${rowKey}-consent-link`}>
        {uiState.kind === "sent" && sentAtLabel
          ? t("_visitorBookings_consentLink_sentOn", { date: sentAtLabel })
          : t("_visitorBookings_consentLink_sendRequiredRole")}
      </Typography>
    );
  }

  if (hideConsentLinkAction) {
    if (uiState.kind === "sent" && sentAtLabel) {
      return (
        <Typography variant="body2" color="text.secondary" data-testid={`${rowKey}-consent-link`}>
          {t("_visitorBookings_consentLink_sentOn", { date: sentAtLabel })}
        </Typography>
      );
    }
    return null;
  }

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}
      data-testid={`${rowKey}-consent-link`}
    >
      {uiState.kind === "sent" && sentAtLabel ? (
        <Typography variant="caption" color="text.secondary">
          {t("_visitorBookings_consentLink_sentOn", { date: sentAtLabel })}
        </Typography>
      ) : null}
      <Button
        size="small"
        variant="outlined"
        disabled={isSending || !resendTarget}
        onClick={handleSend}
        data-testid={`${rowKey}-consent-link-action`}
      >
        {isSending ? <CircularProgress size={18} color="inherit" /> : buttonLabel}
      </Button>
    </Box>
  );
}

function formatConsentTimestamp(
  isoTimestamp: string | undefined,
  timezone: string,
  dateFormat: string
): string | null {
  if (!isoTimestamp) {
    return null;
  }

  const parsed = DateTime.fromISO(isoTimestamp, { zone: "utc" }).setZone(timezone);
  if (!parsed.isValid) {
    return null;
  }
  return parsed.toFormat(`${dateFormat} HH:mm`);
}
